import React from 'react';
import { ScrollView } from 'react-native';
import type { ViewStyle } from 'react-native';

/**
 * The scrolling body of a tab.
 *
 * This exists because of a bug that reached production on every screen but
 * one. React Native defaults `flexGrow` AND `flexShrink` to 0 — unlike CSS,
 * where shrink is 1 — so a `ScrollView` given only a `contentContainerStyle`
 * measures its own frame to its content instead of to the space it was handed.
 * Viewport and content then have the same height, there is nothing to scroll
 * off, and the screen simply does not move under your finger.
 *
 * react-native-web's own ScrollView styles hide this, which is why it looked
 * fine in the browser and on the desktop build the whole time, and was only
 * ever broken on the phone.
 *
 * The cure is `flex: 1` (bound the frame to the parent) plus `minHeight: 0`
 * (let it shrink below its content). Wrapping it in a component rather than
 * repeating those two properties nine times is the point: the next screen
 * someone adds cannot reintroduce this by forgetting them.
 */
export function Screen({
  contentPadding,
  children,
  /** Defaults to 96, clearing the phone's bottom tab bar. */
  paddingBottom = 96,
  contentContainerStyle,
  testID,
}: {
  contentPadding: number;
  children: React.ReactNode;
  paddingBottom?: number;
  contentContainerStyle?: ViewStyle;
  testID?: string;
}) {
  return (
    <ScrollView
      testID={testID}
      style={{ flex: 1, minHeight: 0 }}
      contentContainerStyle={{
        padding: contentPadding,
        paddingBottom,
        ...contentContainerStyle,
      }}
      showsVerticalScrollIndicator={false}
    >
      {children}
    </ScrollView>
  );
}
